// =============================================================================
// WarbachAvatar — SVG portrait of the kobold Warbach, feedback mascot.
//
// Colors and palette are derived from the high-quality reference artwork saved
// at /public/assets/characters/warbach-reference.svg.
//
// Reference palette:
//   Scales (body)  : #c29b5b → #ae884c → #a7855a → #73553b  (sandy amber-tan)
//   Scale shadows  : #6e4824 → #52331d → #462e19             (dark brown)
//   Scale accents  : #ad4e18 → #9c4618                       (deep rusty-orange)
//   Background     : #091c27 → #04222e (dark near-black, teal-tinted cave)
//   Lantern glass  : #71a2b3 → #6cb4ca                       (blue-teal)
//   Lantern flame  : #faf1a6 → #fcf090 → #e49440             (bright warm yellow)
//   Eye iris       : #e49440 → #dabb6f amber slit-pupil
//
// Head faces straight forward — symmetric ears, centered muzzle, both eyes
// at equal size and level. Lantern held at lower-right.
// =============================================================================

interface WarbachAvatarProps {
  /** Diameter in pixels. Defaults to 40. */
  size?: number;
  className?: string;
  /** Add the lantern glow ring animation */
  glowing?: boolean;
}

export default function WarbachAvatar({ size = 40, className = '', glowing = false }: WarbachAvatarProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 40 40"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-label="Warbach the Kobold"
      role="img"
    >
      <defs>
        {/* ── Lantern glow filter ── */}
        <filter id="warbach-glow" x="-30%" y="-30%" width="160%" height="160%">
          <feGaussianBlur stdDeviation="1.2" result="blur" />
          <feComposite in="SourceGraphic" in2="blur" operator="over" />
        </filter>

        {/* ── Lantern flame — bright warm yellow from reference ── */}
        <radialGradient id="lanternFlame" cx="50%" cy="40%" r="55%">
          <stop offset="0%"   stopColor="#faf1a6" stopOpacity="1" />
          <stop offset="50%"  stopColor="#f5da78" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#e49440" stopOpacity="0.2" />
        </radialGradient>

        {/* ── Background — deep dark cave, teal-tinted ── */}
        <radialGradient id="bgGrad" cx="50%" cy="40%" r="65%">
          <stop offset="0%"   stopColor="#0f1e27" />
          <stop offset="100%" stopColor="#04222e" />
        </radialGradient>

        {/* ── Face / head — sandy amber-tan scales, top-lit for front view ── */}
        <radialGradient id="faceGrad" cx="50%" cy="35%" r="65%">
          <stop offset="0%"   stopColor="#cfb07d" />
          <stop offset="55%"  stopColor="#ae884c" />
          <stop offset="100%" stopColor="#6e4824" />
        </radialGradient>

        {/* ── Muzzle — top-lit from front ── */}
        <radialGradient id="snoutGrad" cx="50%" cy="28%" r="65%">
          <stop offset="0%"   stopColor="#dabb6f" />
          <stop offset="55%"  stopColor="#a07d48" />
          <stop offset="100%" stopColor="#69401c" />
        </radialGradient>

        {/* ── Eye glow — amber, from reference golden tones ── */}
        <radialGradient id="eyeGlow" cx="38%" cy="32%" r="58%">
          <stop offset="0%"   stopColor="#f5da78" />
          <stop offset="55%"  stopColor="#e49440" />
          <stop offset="100%" stopColor="#7e2811" />
        </radialGradient>

        {/* ── Clip to circle ── */}
        <clipPath id="circleClip">
          <circle cx="20" cy="20" r="20" />
        </clipPath>
      </defs>

      {/* ── Background circle — teal-dark cave ── */}
      <circle cx="20" cy="20" r="20" fill="url(#bgGrad)" />

      <g clipPath="url(#circleClip)">

        {/* ── Ambient lantern glow — radiates from bottom-right ── */}
        <ellipse cx="31" cy="33" rx="10" ry="8" fill="#e49440" opacity="0.08" />

        {/* ── Body / tunic — dark teal, symmetric ── */}
        <path d="M4,44 C6,31 12,27 20,27 C28,27 34,31 36,44 Z" fill="#1c3f4d" />
        {/* Tunic centre shadow */}
        <path d="M17,44 C17,33 18,28 20,27 C22,28 23,33 23,44 Z" fill="#0c2b36" opacity="0.45" />

        {/* ── Neck — centred ── */}
        <ellipse cx="20" cy="28.5" rx="4.5" ry="3" fill="#94703d" />

        {/* ══════════════════════════════════════════════════════════════════
            EARS — symmetric, equal size, both pointing upward
        ══════════════════════════════════════════════════════════════════ */}
        {/* Left ear outer */}
        <path d="M 11,13 L 9,2 L 17,10 Z" fill="#8e6731" />
        {/* Left ear inner membrane */}
        <path d="M 12,12.5 L 10.5,5 L 16,10 Z" fill="#512f12" opacity="0.7" />
        {/* Right ear outer */}
        <path d="M 29,13 L 31,2 L 23,10 Z" fill="#8e6731" />
        {/* Right ear inner membrane */}
        <path d="M 28,12.5 L 29.5,5 L 24,10 Z" fill="#512f12" opacity="0.7" />

        {/* ── Full head / cranium — centred, front-facing ── */}
        <ellipse cx="20" cy="18" rx="9.5" ry="11" fill="url(#faceGrad)" />

        {/* ── Scale texture — symmetric arcs across full head ── */}
        <path d="M 13,10.5 Q 20,8.5 27,10.5"   stroke="#6e4824" strokeWidth="0.45" fill="none" opacity="0.5" />
        <path d="M 12,13   Q 20,11   28,13"     stroke="#6e4824" strokeWidth="0.45" fill="none" opacity="0.45" />
        <path d="M 11,15.5 Q 15.5,14.5 20,15.5 Q 24.5,14.5 29,15.5"
              stroke="#6e4824" strokeWidth="0.5"  fill="none" opacity="0.55" />
        <path d="M 11,18   Q 15,17   20,18   Q 25,17   29,18"
              stroke="#6e4824" strokeWidth="0.45" fill="none" opacity="0.5" />
        <path d="M 12,20.5 Q 16,19.5 20,20.5 Q 24,19.5 28,20.5"
              stroke="#6e4824" strokeWidth="0.4"  fill="none" opacity="0.45" />
        {/* Brow ridge — symmetric */}
        <path d="M 11,14 Q 15.5,12.2 20,13.5 Q 24.5,12.2 29,14"
              stroke="#52331d" strokeWidth="0.65" fill="none" opacity="0.55" />

        {/* ── Dorsal crest — 3 spines, symmetric around x=20 ── */}
        <path d="M 15.5,10 L 16,7.5 L 17.5,10"   fill="#8e6731" opacity="0.9" />
        <path d="M 19,9   L 20,6.5 L 21,9"        fill="#a07d48" opacity="0.9" />
        <path d="M 22.5,10 L 24,7.5 L 24.5,10"   fill="#8e6731" opacity="0.85" />

        {/* ══════════════════════════════════════════════════════════════════
            MUZZLE — forward-facing, centred at x=20.
            Shown as a rounded protrusion below the eye line.
        ══════════════════════════════════════════════════════════════════ */}
        {/* Main muzzle mass */}
        <ellipse cx="20" cy="23.5" rx="5.5" ry="4" fill="url(#snoutGrad)" />
        {/* Upper muzzle highlight — top-lit */}
        <ellipse cx="20" cy="21.5" rx="5" ry="2" fill="#cfb07d" opacity="0.35" />
        {/* Central ridge — vertical line down muzzle */}
        <line x1="20" y1="20.5" x2="20" y2="25" stroke="#6e4824" strokeWidth="0.45" opacity="0.4" />
        {/* Horizontal scale rows across muzzle */}
        <path d="M 16,22.5 Q 20,21.5 24,22.5" stroke="#6e4824" strokeWidth="0.35" fill="none" opacity="0.4" />
        <path d="M 15.5,24.5 Q 20,23.5 24.5,24.5" stroke="#6e4824" strokeWidth="0.3"  fill="none" opacity="0.35" />
        {/* Jaw line */}
        <path d="M 15.5,26.5 Q 17.5,27.8 20,27.5 Q 22.5,27.8 24.5,26.5"
              stroke="#52331d" strokeWidth="0.7" fill="none" strokeLinecap="round" />
        {/* Tooth hints — symmetric */}
        <line x1="17.5" y1="27"  x2="17.5" y2="28"  stroke="#e2c88b" strokeWidth="0.5" opacity="0.6" />
        <line x1="20"   y1="27.5" x2="20"  y2="28.5" stroke="#e2c88b" strokeWidth="0.5" opacity="0.55" />
        <line x1="22.5" y1="27"  x2="22.5" y2="28"  stroke="#e2c88b" strokeWidth="0.5" opacity="0.55" />
        {/* Nostrils — symmetric */}
        <ellipse cx="18.3" cy="22.8" rx="0.7" ry="0.5" fill="#3d2310" />
        <ellipse cx="21.7" cy="22.8" rx="0.7" ry="0.5" fill="#3d2310" />

        {/* ── Eyes — symmetric, same size, same height ── */}
        {/* Left eye socket */}
        <ellipse cx="14.5" cy="16.5" rx="2.4" ry="2.1" fill="#211b19" />
        {/* Left iris */}
        <ellipse cx="14.5" cy="16.5" rx="1.75" ry="1.55" fill="url(#eyeGlow)" />
        {/* Left slit pupil */}
        <ellipse cx="14.5" cy="16.5" rx="0.4"  ry="1.1"  fill="#0c1219" />
        <circle  cx="13.8" cy="15.8" r="0.45"              fill="white"  opacity="0.7" />
        <circle  cx="15.3" cy="17.4" r="0.25"              fill="white"  opacity="0.3" />

        {/* Right eye socket */}
        <ellipse cx="25.5" cy="16.5" rx="2.4" ry="2.1" fill="#211b19" />
        {/* Right iris */}
        <ellipse cx="25.5" cy="16.5" rx="1.75" ry="1.55" fill="url(#eyeGlow)" />
        {/* Right slit pupil */}
        <ellipse cx="25.5" cy="16.5" rx="0.4"  ry="1.1"  fill="#0c1219" />
        <circle  cx="24.8" cy="15.8" r="0.45"              fill="white"  opacity="0.7" />
        <circle  cx="26.3" cy="17.4" r="0.25"              fill="white"  opacity="0.3" />

        {/* ══════════════════════════════════════════════════════════════════
            LANTERN — held at lower-right. Iron frame, blue-teal glass,
            bright yellow flame.
        ══════════════════════════════════════════════════════════════════ */}
        {/* Hanging chain / arm */}
        <path d="M30.5,28 L30.5,26 C30.5,25 31,24.5 31.5,24.5"
              stroke="#4b3f34" strokeWidth="0.8" fill="none" />
        {/* Outer lantern frame — dark iron */}
        <rect x="27" y="28" width="7"  height="9" rx="1.5" fill="#4b3f34" />
        {/* Glass panel — blue-teal */}
        <rect x="27.8" y="28.8" width="5.4" height="7.4" rx="1" fill="#487588" />
        {/* Flame glow through glass */}
        <rect x="28.2" y="29.2" width="4.6" height="6.6" rx="0.8"
              fill="url(#lanternFlame)" filter="url(#warbach-glow)" />
        {/* Frame bars */}
        <line x1="30.5" y1="28.8" x2="30.5" y2="36.2" stroke="#4b3f34" strokeWidth="0.7" />
        <line x1="27.8" y1="32"   x2="33.2" y2="32"   stroke="#4b3f34" strokeWidth="0.5" opacity="0.7" />
        {/* Top cap */}
        <path d="M27.5,28.5 L30.5,26.5 L33.5,28.5" fill="#4b3f34" />
        {/* Ambient warm glow */}
        <ellipse cx="30" cy="29" rx="6" ry="5" fill="#e49440" opacity="0.06" />
      </g>

      {/* ── Outer border ring — amber/golden tone of lantern ── */}
      <circle
        cx="20"
        cy="20"
        r="19"
        fill="none"
        stroke="#ae884c"
        strokeWidth={glowing ? "1.5" : "1"}
        opacity={glowing ? "0.85" : "0.5"}
      />

      {/* ── Glowing pulse ring (optional) ── */}
      {glowing && (
        <circle
          cx="20"
          cy="20"
          r="19.5"
          fill="none"
          stroke="#dabb6f"
          strokeWidth="1"
          opacity="0.4"
          className="animate-ping"
          style={{ transformOrigin: '20px 20px' }}
        />
      )}
    </svg>
  );
}
